import { Injector, Input, Directive } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { KalgudiDialogsService } from '@kalgudi/common';
import { KalgudiDestroyable } from '@kalgudi/core';
import { KalgudiNotification, KL_NOTIFICATION } from '@kalgudi/core/config';
import { KalgudiDialogConfig, KalgudiDialogResult, KalgudiUserCertificateDetails } from '@kalgudi/types';
import { Observable } from 'rxjs';
import { filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  KalgudiProfileCertificatesService,
} from '../../../components/sections/certifications/kalgudi-profile-certificates.service';
import { KalgudiProfileActionService } from '../../../services/kalgudi-profile-action.service';
import { KalgudiProfileService } from '../../../services/kalgudi-profile.service';

@Directive()
export abstract class FarmerCertificates  extends KalgudiDestroyable {

  @Input()
  certificates: KalgudiUserCertificateDetails[];

  editable: boolean;

  private dialogsService: KalgudiDialogsService;
  private certificateService: KalgudiProfileCertificatesService;
  private notification: KalgudiNotification;
  private profileActionService: KalgudiProfileActionService;
  private profileService: KalgudiProfileService;

  constructor(protected injector: Injector) {

    super();

    this.dialogsService       = this.injector.get(KalgudiDialogsService);
    this.certificateService   = this.injector.get(KalgudiProfileCertificatesService);
    this.notification         = this.injector.get<KalgudiNotification>(KL_NOTIFICATION);
    this.profileActionService = this.injector.get(KalgudiProfileActionService);
    this.profileService       = this.injector.get(KalgudiProfileService);


    this.profileService.editable$
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        res => this.editable = res
      );

  }

  // --------------------------------------------------------
  // #region Public methods
  // --------------------------------------------------------

  /**
   * Deletes certificate
   */
  deleteCertificate(certificateId: string) {

    this.showDeleteConfirmationDialog()
      .pipe(
        tap(res => this.notification.showSpinner()),

        switchMap(res => this.certificateService.deleteCertificate(certificateId)),

        finalize(() => this.notification.hideSpinner())
      )
      .subscribe(
        res => this.onCertificateDeleted(res),
        err => this.onCertificateFailed(err)
      );
  }

  /**
   * Updates certificate
   */
  updateCertificate(certificateId: string, certificateDetails: KalgudiUserCertificateDetails): Observable<KalgudiDialogResult> {
    return this.profileActionService.updateCertificate(certificateId, certificateDetails)
      .pipe(
        takeUntil(this.destroyed$)
      );
  }

  /**
   * Shows add certificate dialog
   */
  showAddCertificateDialog(): Observable<KalgudiDialogResult> {

    if (!this.editable) {

      return;
    }

    return this.profileActionService.showAddCertificateDialog()
      .pipe(
        takeUntil(this.destroyed$)
      );
  }

  // --------------------------------------------------------
  // #endregion
  // --------------------------------------------------------


  // --------------------------------------------------------
  // #region Private and protected methods
  // --------------------------------------------------------


  /**
   * Certificate deleted successfully
   */
  protected onCertificateDeleted(res: boolean) {
    this.notification.showMessage('Certificate deleted');
  }

  /**
   * Failed to deleted certificate
   */
  protected onCertificateFailed(err) {
    this.notification.showMessage('Failed to delete certificate, please try again later!');
  }


  /**
   * Shows confirmation dialog and returns boolean.
   */
  private showDeleteConfirmationDialog(): Observable<boolean> {

    // Input dialog UI configuration
    const dialogDetails: KalgudiDialogConfig = {
      title: 'Confirm delete',
      acceptButtonTitle: 'Delete',
      rejectButtonTitle: 'Cancel',
      message: 'Are you sure you want to delete?',
      matIcon: 'warning',
      iconColor: 'warn',
      data: {}
    };

    // Material dialog configuration
    const dialogConfig: MatDialogConfig = {
      width: '600px',
      maxWidth: '600px',
      hasBackdrop: true,
      disableClose: true,
      autoFocus: false,
    };

    return this.dialogsService.showConfirm(dialogDetails, dialogConfig)
      .pipe(

        // Filter only accepted actions, do nothing for cancel actions
        filter(r => r.accepted),

        // Transform the partial data to boolean whether confirmation accepted or rejected
        map(r => r.accepted),
      );
  }


  // --------------------------------------------------------
  // #endregion
  // --------------------------------------------------------

}
